// =========================================================================
// Building a broadcastable TRON transaction.
//
// The missing half. We could decode a transaction rigorously and encode the
// TRC-20 call data, but nothing constructed a transaction from a live block
// reference — so nothing could actually move a coin.
//
// WHY WE SERIALISE RATHER THAN ASK THE NODE. A node will happily build a
// transaction via /wallet/triggersmartcontract, and that would avoid this file
// entirely. Two things make it unusable:
//
//   1. The node sets its own expiration — 60 seconds by default. The whole
//      ceremony is "walk to a safe, connect a device, compare a txID, tap".
//      Sixty seconds is not that. We must set expiration ourselves, and
//      changing it means re-serialising raw_data, which means owning the
//      serialiser.
//   2. (This said a node does not set Permission_id. It does, when asked —
//      checked on Nile, 2 October. The expiration is reason enough.)
//
// INDEPENDENCE FROM THE DECODER IS DELIBERATE. This file shares no helpers
// with tron-tx.mjs. A round-trip test between an encoder and a decoder that
// share a varint implementation proves only that the shared code is
// self-consistent — a symmetric bug cancels out and the test still passes. The
// real check is in the tests: take REAL transactions off the chain, and
// re-encode them byte-for-byte. TRON's own serialiser is the reference, not
// ours.
// =========================================================================

import { sha256 } from '@noble/hashes/sha256';
import { decodeTronAddress } from './address.mjs';

// Transaction.raw
const RAW = { REF_BLOCK_BYTES: 1, REF_BLOCK_HASH: 4, EXPIRATION: 8, CONTRACT: 11, TIMESTAMP: 14, FEE_LIMIT: 18 };
// Transaction.Contract
const CONTRACT = { TYPE: 1, PARAMETER: 2, PERMISSION_ID: 5 };
// google.protobuf.Any
const ANY = { TYPE_URL: 1, VALUE: 2 };
// TriggerSmartContract
const TSC = { OWNER: 1, CONTRACT: 2, DATA: 4 };

export const CONTRACT_TYPE_TRIGGER_SMART_CONTRACT = 31;
export const TYPE_URL_TRIGGER = 'type.googleapis.com/protocol.TriggerSmartContract';
// TransferContract (plain TRX)
const TRANSFER = { OWNER: 1, TO: 2, AMOUNT: 3 };
export const CONTRACT_TYPE_TRANSFER = 1;
export const TYPE_URL_TRANSFER = 'type.googleapis.com/protocol.TransferContract';

// ---- protobuf primitives -------------------------------------------------

function varint(value) {
  let v = BigInt(value);
  if (v < 0n) throw new Error('negative varint');
  const out = [];
  do {
    let byte = Number(v & 0x7fn);
    v >>= 7n;
    if (v > 0n) byte |= 0x80;
    out.push(byte);
  } while (v > 0n);
  return Uint8Array.from(out);
}

function concat(chunks) {
  const total = chunks.reduce((n, c) => n + c.length, 0);
  const out = new Uint8Array(total);
  let at = 0;
  for (const c of chunks) { out.set(c, at); at += c.length; }
  return out;
}

const tag = (field, wire) => varint((field << 3) | wire);
/** varint field */
const fVar = (field, value) => concat([tag(field, 0), varint(value)]);
/** length-delimited field */
const fLen = (field, bytes) => concat([tag(field, 2), varint(bytes.length), bytes]);

const utf8 = (s) => new TextEncoder().encode(s);

export function hexToBytes(hex) {
  const clean = String(hex).startsWith('0x') ? String(hex).slice(2) : String(hex);
  if (!/^[0-9a-fA-F]*$/.test(clean) || clean.length % 2 !== 0) throw new Error(`not hex: ${hex}`);
  const out = new Uint8Array(clean.length / 2);
  for (let i = 0; i < out.length; i++) out[i] = parseInt(clean.slice(i * 2, i * 2 + 2), 16);
  return out;
}

export const bytesToHex = (b) => [...b].map((x) => x.toString(16).padStart(2, '0')).join('');

/**
 * Accept an address as base58 ("T…") or as the 21-byte on-wire hex ("41…").
 *
 * Callers in this system pass base58 — it is what an operator reads and what
 * the address book stores. The chain speaks the 21-byte form, so anything
 * reconstructing a transaction from chain bytes needs that too, and silently
 * mis-parsing one as the other would build a transfer to a real address that
 * belongs to nobody.
 */
function addressBytes(address) {
  if (typeof address !== 'string' || address.length === 0) {
    throw new Error('address must be a non-empty string');
  }
  if (/^41[0-9a-fA-F]{40}$/.test(address)) return hexToBytes(address);
  const bytes = decodeTronAddress(address);
  if (bytes.length !== 21 || bytes[0] !== 0x41) throw new Error('address did not decode to a 21-byte TRON address');
  return bytes;
}

// ---- TRC-20 transfer call data ------------------------------------------

/** keccak256("transfer(address,uint256)")[0..4] */
const TRANSFER_SELECTOR = 'a9059cbb';

/**
 * ABI-encode transfer(address,uint256).
 *
 * The address is the 20-byte body WITHOUT TRON's 0x41 prefix, left-padded to
 * 32 bytes — an EVM address in TRON clothing. Getting this wrong produces a
 * transfer to a valid-looking address that belongs to nobody.
 */
export function encodeTransferData(toAddress, amountBaseUnits) {
  const body = addressBytes(toAddress).slice(1); // drop the 0x41 prefix
  if (body.length !== 20) throw new Error('recipient did not decode to 20 bytes');

  // A bigint and nothing else, as encodeTrc20Transfer requires. BigInt() would
  // take `true` as 1 base unit and a Number past 2^53 already rounded
  // (review finding A4).
  if (typeof amountBaseUnits !== 'bigint') {
    throw new Error('amount must be a bigint of base units — a Number may already have lost precision');
  }
  const amount = amountBaseUnits;
  if (amount <= 0n) throw new Error('amount must be positive');
  if (amount >= 1n << 256n) throw new Error('amount exceeds uint256');

  const addrWord = bytesToHex(body).padStart(64, '0');
  const amountWord = amount.toString(16).padStart(64, '0');
  return hexToBytes(TRANSFER_SELECTOR + addrWord + amountWord);
}

// ---- block reference -----------------------------------------------------

/**
 * Derive ref_block_bytes and ref_block_hash from a block.
 *
 * TRON uses a short reference rather than a full hash: bytes 6-8 of the block
 * NUMBER and bytes 8-16 of the block ID. A transaction referencing a block the
 * chain has since reorganised past is rejected — which is a feature, and part
 * of why an expiration that outlives the reference window is pointless.
 *
 * @param {{ number: number|string|bigint, blockId: string }} block
 */
export function blockReference(block) {
  const num = BigInt(block.number);
  if (num <= 0n) throw new Error('block number must be positive');

  const numBytes = new Uint8Array(8);
  new DataView(numBytes.buffer).setBigUint64(0, num, false);

  const idBytes = hexToBytes(block.blockId);
  if (idBytes.length !== 32) throw new Error('blockId must be 32 bytes');

  return {
    refBlockBytes: numBytes.slice(6, 8),
    refBlockHash: idBytes.slice(8, 16),
  };
}

// ---- the transaction -----------------------------------------------------

/**
 * Serialise Transaction.raw.
 *
 * FIELD ORDER IS ASCENDING BY FIELD NUMBER. Protobuf parsers accept any order,
 * so a wrong order still decodes — and still produces a DIFFERENT txID from the
 * one TRON would compute for the same logical transaction. Since the txID is
 * what an operator compares against a Ledger screen, and what every idempotency
 * key in this system is built from, "parses fine" is not the bar. Byte-identity
 * with TRON's own serialiser is, and the tests hold it to that against real
 * chain data.
 */
export function encodeRawData({
  refBlockBytes, refBlockHash, expiration, contractBytes, timestamp, feeLimit,
}) {
  const parts = [
    fLen(RAW.REF_BLOCK_BYTES, refBlockBytes),
    fLen(RAW.REF_BLOCK_HASH, refBlockHash),
    fVar(RAW.EXPIRATION, expiration),
    fLen(RAW.CONTRACT, contractBytes),
    fVar(RAW.TIMESTAMP, timestamp),
  ];
  // fee_limit is omitted entirely when zero — TRON does the same, and an
  // explicit zero would change the bytes and therefore the txID.
  if (BigInt(feeLimit) > 0n) parts.push(fVar(RAW.FEE_LIMIT, feeLimit));
  return concat(parts);
}

// The permission a transaction is signed under: 0 is the owner, 1 a super
// representative's witness, 2-9 the up-to-eight actives in the order the chain
// numbered them. Anything else is refused rather than left out, because a
// missing field is read as 0 — the owner (review finding A5).
function permissionIdOf(permissionId) {
  if (!Number.isInteger(permissionId) || permissionId < 0 || permissionId > 9) {
    throw new Error(`permissionId must be a whole number from 0 to 9 — got ${permissionId}`);
  }
  return permissionId;
}

/** Serialise Transaction.Contract wrapping a TriggerSmartContract. */
export function encodeTriggerContract({ ownerAddress, contractAddress, data, permissionId }) {
  permissionIdOf(permissionId);
  const tsc = concat([
    fLen(TSC.OWNER, addressBytes(ownerAddress)),
    fLen(TSC.CONTRACT, addressBytes(contractAddress)),
    fLen(TSC.DATA, data),
  ]);

  const any = concat([
    fLen(ANY.TYPE_URL, utf8(TYPE_URL_TRIGGER)),
    fLen(ANY.VALUE, tsc),
  ]);

  const parts = [
    fVar(CONTRACT.TYPE, CONTRACT_TYPE_TRIGGER_SMART_CONTRACT),
    fLen(CONTRACT.PARAMETER, any),
  ];
  // Permission_id 0 is the owner permission and TRON omits the field for it.
  // Emitting an explicit 0 changes the bytes and so the txID.
  if (permissionId > 0) parts.push(fVar(CONTRACT.PERMISSION_ID, permissionId));
  return concat(parts);
}

/** Serialise Transaction.Contract wrapping a TransferContract (plain TRX, in sun). */
export function encodeTransferContract({ ownerAddress, to, amountSun, permissionId = 0 }) {
  permissionIdOf(permissionId);
  if (typeof amountSun !== 'bigint' || amountSun < 0n) throw new Error('amountSun must be a bigint of sun, zero or more');
  const parts = [fLen(TRANSFER.OWNER, addressBytes(ownerAddress)), fLen(TRANSFER.TO, addressBytes(to))];
  // Protobuf omits a zero amount; so does TRON's serialiser.
  if (amountSun > 0n) parts.push(fVar(TRANSFER.AMOUNT, amountSun));
  const any = concat([fLen(ANY.TYPE_URL, utf8(TYPE_URL_TRANSFER)), fLen(ANY.VALUE, concat(parts))]);
  const out = [fVar(CONTRACT.TYPE, CONTRACT_TYPE_TRANSFER), fLen(CONTRACT.PARAMETER, any)];
  if (permissionId > 0) out.push(fVar(CONTRACT.PERMISSION_ID, permissionId));
  return concat(out);
}

/**
 * A plain TRX transfer from a block reference and the times the caller sets
 * — the raw bytes, their txID and the JSON half. Nothing here decides whether
 * it can be sent: a caller that wants one the chain will never take (the key
 * proof, signer/src/key-approval.mjs) passes an expiration before its
 * timestamp and a reference no block has.
 */
export function buildTrxTransfer({ ownerAddress, to, amountSun, reference, timestamp, expiration, permissionId = 0 }) {
  if (!Number.isInteger(timestamp) || !Number.isInteger(expiration)) throw new Error('timestamp and expiration must be whole epoch-ms numbers');
  const { refBlockBytes, refBlockHash } = reference;
  if (!(refBlockBytes instanceof Uint8Array) || refBlockBytes.length !== 2 || !(refBlockHash instanceof Uint8Array) || refBlockHash.length !== 8) {
    throw new Error('reference must carry refBlockBytes (2 bytes) and refBlockHash (8 bytes)');
  }
  const contractBytes = encodeTransferContract({ ownerAddress, to, amountSun, permissionId });
  const raw = encodeRawData({ refBlockBytes, refBlockHash, expiration, contractBytes, timestamp, feeLimit: 0 });
  const contract = {
    parameter: { value: { owner_address: bytesToHex(addressBytes(ownerAddress)), to_address: bytesToHex(addressBytes(to)), ...(amountSun > 0n ? { amount: Number(amountSun) } : {}) }, type_url: TYPE_URL_TRANSFER },
    type: 'TransferContract',
  };
  if (permissionId > 0) contract.Permission_id = permissionId;
  return {
    txID: bytesToHex(sha256(raw)),
    raw_data_hex: bytesToHex(raw),
    raw_data: { contract: [contract], ref_block_bytes: bytesToHex(refBlockBytes), ref_block_hash: bytesToHex(refBlockHash), expiration, timestamp },
    visible: false,
  };
}

/**
 * The JSON half of a transaction: a rendering of EXACTLY the bytes in
 * raw_data_hex, in the form a node returns with `visible: false`.
 *
 * This half is not decoration, and the previous version treated it as such.
 * It carried the two addresses — in base58, under `visible: true` — and no call
 * data at all, beside a comment saying nothing trusted it. Two things do:
 *
 *   - TronWeb's multiSign, which TronLink runs before it will ask a Ledger for
 *     anything, rebuilds the protobuf from THIS JSON and refuses the
 *     transaction ("Invalid transaction") unless the result is byte-identical
 *     to raw_data_hex and hashes to txID. It reads TriggerSmartContract
 *     addresses as hex, whatever `visible` says.
 *   - A node's /wallet/broadcasttransaction builds the transaction it
 *     broadcasts from this JSON and never reads raw_data_hex.
 *
 * So every field that is in the bytes is here, in hex, and nothing that is not.
 * Fields protobuf omits when zero (Permission_id, fee_limit) are omitted here
 * too, as TRON's own rendering does.
 */
function rawDataJson({
  ownerHex, contractHex, dataHex, permissionId, refBlockBytes, refBlockHash, expiration, timestamp, feeLimit,
}) {
  const contract = {
    parameter: {
      value: { data: dataHex, owner_address: ownerHex, contract_address: contractHex },
      type_url: TYPE_URL_TRIGGER,
    },
    type: 'TriggerSmartContract',
  };
  if (permissionId > 0) contract.Permission_id = permissionId;
  const raw = {
    contract: [contract],
    ref_block_bytes: bytesToHex(refBlockBytes),
    ref_block_hash: bytesToHex(refBlockHash),
    expiration,
    timestamp,
  };
  if (BigInt(feeLimit) > 0n) raw.fee_limit = Number(feeLimit);
  return raw;
}

/**
 * Build an unsigned TRC-20 transfer, ready for a Ledger.
 *
 * Returns the shape TronLink's multiSign expects — the bytes, the txID
 * recomputed from them, and a JSON half that renders those same bytes (see
 * rawDataJson above for why that last part is load-bearing).
 */
export function buildTrc20Transfer({
  ownerAddress,
  contractAddress,
  to,
  amount,
  block,
  expiresInMs = 30 * 60_000,
  feeLimitSun = 40_000_000,
  permissionId = 2,
  now = Date.now(),
}) {
  if (!Number.isFinite(now)) throw new Error('now must be a finite epoch-ms number');
  if (!Number.isFinite(expiresInMs) || expiresInMs <= 0) throw new Error('expiresInMs must be positive');
  permissionIdOf(permissionId);

  // TRON rejects anything beyond 24h; our own ceilings are far tighter, and
  // this is only the last-ditch bound.
  if (expiresInMs > 24 * 3600_000) throw new Error('expiration beyond the 24h protocol maximum');

  const { refBlockBytes, refBlockHash } = blockReference(block);
  const timestamp = Math.floor(now);
  const expiration = timestamp + Math.floor(expiresInMs);

  const data = encodeTransferData(to, amount);
  const contractBytes = encodeTriggerContract({ ownerAddress, contractAddress, data, permissionId });

  const raw = encodeRawData({
    refBlockBytes, refBlockHash, expiration, contractBytes, timestamp, feeLimit: feeLimitSun,
  });

  const rawDataHex = bytesToHex(raw);
  const txID = bytesToHex(sha256(raw));

  return {
    txID,
    raw_data_hex: rawDataHex,
    raw_data: rawDataJson({
      ownerHex: bytesToHex(addressBytes(ownerAddress)),
      contractHex: bytesToHex(addressBytes(contractAddress)),
      dataHex: bytesToHex(data),
      permissionId, refBlockBytes, refBlockHash, expiration, timestamp, feeLimit: feeLimitSun,
    }),
    // false: the addresses above are hex. `true` told every reader to expect
    // base58, which TronWeb ignores for this contract type and a node obeys.
    visible: false,
  };
}
